"use client";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { CustomiseDrawer } from "./customise-drawer";

interface CustomiseDrawerValue {
  isOpen: boolean;
  open: () => void;
  close: () => void;
}

const CustomiseDrawerContext = createContext<CustomiseDrawerValue | undefined>(
  undefined,
);

/**
 * Holds the Customise sheet open/closed for the whole studio.
 *
 * The sheet has several triggers in different trees — the studio header,
 * Console's sidebar footer, the sidebar's own button — and they are server
 * components' children, so they cannot share a `useState` between them. One
 * provider near the root is what lets any of them open the same sheet.
 *
 * Mounted for every studio, and inert until something opens it:
 * the sheet's contents (and the tRPC queries the areas list runs) only mount
 * while it is open, and the triggers exist only inside the flag-gated layouts.
 *
 * Must sit INSIDE ThemeProvider — the sheet previews the arrangement through
 * it — and inside StudioTrpcProvider, which is where its writes go.
 */
export function CustomiseDrawerProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);

  const value = useMemo<CustomiseDrawerValue>(
    () => ({ isOpen, open, close }),
    [isOpen, open, close],
  );

  return (
    <CustomiseDrawerContext.Provider value={value}>
      {children}
      <CustomiseDrawer open={isOpen} onOpenChange={setIsOpen} />
    </CustomiseDrawerContext.Provider>
  );
}

export function useCustomiseDrawer(): CustomiseDrawerValue {
  const context = useContext(CustomiseDrawerContext);
  if (context === undefined) {
    throw new Error(
      "useCustomiseDrawer must be used within a CustomiseDrawerProvider",
    );
  }
  return context;
}
